import { forwardRef } from 'react';
import type {
  MenuDividerProps as MantineMenuDividerProps,
  MenuDropdownProps as MantineMenuDropdownProps,
  MenuItemProps as MantineMenuItemProps,
  MenuLabelProps as MantineMenuLabelProps,
  MenuProps as MantineMenuProps,
  MenuRadioItemProps as MantineMenuRadioItemProps,
  MenuSubDropdownProps as MantineMenuSubDropdownProps,
  MenuSubItemProps as MantineMenuSubItemProps,
  MenuSubProps as MantineMenuSubProps,
} from '@mantine/core';
import { createPolymorphicComponent, Menu as MantineMenu } from '@mantine/core';

import type { LockedPolymorphic, LockedProps } from '../types';

export type MenuProps = Omit<MantineMenuProps, LockedProps>;

type MenuDropdownProps = Omit<MantineMenuDropdownProps, LockedProps>;

type MenuItemProps = Omit<MantineMenuItemProps, LockedProps>;

type MenuLabelProps = Omit<MantineMenuLabelProps, LockedProps>;

type MenuDividerProps = Omit<MantineMenuDividerProps, LockedProps>;

type MenuRadioItemProps = Omit<MantineMenuRadioItemProps, LockedProps>;

const MenuRoot = (props: MenuProps) => {
  return <MantineMenu {...props} />;
};

// Assigning the raw Mantine parts left every one of them unlocked, so the `Omit` on `MenuProps`
// only ever covered the root. Menu coordinates its parts through context and finds items for
// keyboard navigation by DOM query (`[data-menu-item]`), never by child identity, so wrapping each
// part is transparent.
const MenuDropdown = forwardRef<HTMLDivElement, MenuDropdownProps>((props, ref) => {
  return <MantineMenu.Dropdown {...props} ref={ref} />;
});

MenuDropdown.displayName = 'Menu.Dropdown';

const _MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>((props, ref) => {
  return <MantineMenu.Item {...props} ref={ref} />;
});

_MenuItem.displayName = 'Menu.Item';

// Mantine's own Menu.Item is polymorphic and several call sites render it as a link, so keep
// `component=` working — which means the lock needs the re-narrowing cast, not just the `Omit`.
const MenuItem = createPolymorphicComponent<'button', MenuItemProps>(
  _MenuItem,
) as LockedPolymorphic<'button', MenuItemProps>;

const MenuLabel = forwardRef<HTMLDivElement, MenuLabelProps>((props, ref) => {
  return <MantineMenu.Label {...props} ref={ref} />;
});

MenuLabel.displayName = 'Menu.Label';

const MenuDivider = forwardRef<HTMLDivElement, MenuDividerProps>((props, ref) => {
  return <MantineMenu.Divider {...props} ref={ref} />;
});

MenuDivider.displayName = 'Menu.Divider';

// A single-select menu: the group holds the value, each item renders as `menuitemradio` with
// `aria-checked` and only fires `onChange` when it isn't already the selected one. Mantine's radio
// items default to leaving the menu open (unlike `Menu.Item`, which the root closes on click); the
// default lands before the spread so a call site can still opt back out.
const MenuRadioItem = forwardRef<HTMLButtonElement, MenuRadioItemProps>((props, ref) => {
  return <MantineMenu.RadioItem closeMenuOnClick {...props} ref={ref} />;
});

MenuRadioItem.displayName = 'Menu.RadioItem';

type MenuSubProps = Omit<MantineMenuSubProps, LockedProps>;

type MenuSubItemProps = Omit<MantineMenuSubItemProps, LockedProps>;

type MenuSubDropdownProps = Omit<MantineMenuSubDropdownProps, LockedProps>;

// Sub-menus coordinate with the root the same way the other parts do — through context and the
// `[data-menu-item]` query — so wrapping each part stays transparent. The sub root renders no
// element of its own; its `Omit` matters for `shadow` on the floating panel.
const MenuSubRoot = (props: MenuSubProps) => {
  return <MantineMenu.Sub {...props} />;
};

const MenuSubItem = forwardRef<HTMLButtonElement, MenuSubItemProps>((props, ref) => {
  return <MantineMenu.Sub.Item {...props} ref={ref} />;
});

MenuSubItem.displayName = 'Menu.Sub.Item';

const MenuSubDropdown = forwardRef<HTMLDivElement, MenuSubDropdownProps>((props, ref) => {
  return <MantineMenu.Sub.Dropdown {...props} ref={ref} />;
});

MenuSubDropdown.displayName = 'Menu.Sub.Dropdown';

const MenuSub = Object.assign(MenuSubRoot, {
  Dropdown: MenuSubDropdown,
  Item: MenuSubItem,
  // Like Menu.Target: clones the child it's given, renders nothing of its own, nothing to lock.
  Target: MantineMenu.Sub.Target,
});

export const Menu = Object.assign(MenuRoot, {
  Divider: MenuDivider,
  Dropdown: MenuDropdown,
  Item: MenuItem,
  Label: MenuLabel,
  // Menu.RadioGroup takes only `value`/`defaultValue`/`onChange`/`children` — it renders no element
  // of its own, so there is no style surface to lock.
  RadioGroup: MantineMenu.RadioGroup,
  RadioItem: MenuRadioItem,
  Sub: MenuSub,
  // Menu.Target takes only `children` and `refProp` — it renders no element of its own and clones
  // the child it's given, so there is no style surface to lock.
  Target: MantineMenu.Target,
});
